import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, ArrowUpRight } from 'lucide-react'
import { getMyInhouseRequests, type InhouseRequest } from '@/services/inhouseService'

// สถานะที่ลูกค้าต้องรู้มีแค่ 2 แบบ แยกจาก req.status (สถานะติดตามงานภายในของแอดมิน) ให้ชัดเจน:
//   - ยังไม่กดยอมรับใบเสนอราคา (accepted_at ว่าง) → ต้องกดยืนยันเองก่อน ทีมงานยังไม่รู้เรื่อง
//   - กดยอมรับแล้ว (accepted_at มีค่า) → ระบบแจ้งทีมงานไปแล้ว รอทีมงานติดต่อกลับอย่างเดียว
const requestDisplayStatus = (req: InhouseRequest) =>
  req.accepted_at
    ? { label: 'ยอมรับใบเสนอราคาแล้ว รอทีมงานติดต่อกลับ', dot: 'bg-accent' }
    : { label: 'รอคุณยืนยันรับใบเสนอราคา', dot: 'bg-amber-500' }

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })

export default function InhouseTab() {
  const [requests, setRequests] = useState<InhouseRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    getMyInhouseRequests()
      .then(setRequests)
      .catch((err) => setError(err instanceof Error ? err.message : 'โหลดคำขอไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <p className="mb-6 text-sm text-[#69738A]">คำขอใบเสนอราคา In-house ของคุณ ({requests.length})</p>

      {loading ? (
        <p className="text-sm text-[#69738A]">กำลังโหลด...</p>
      ) : error ? (
        <p className="text-sm text-rose-500">{error}</p>
      ) : requests.length === 0 ? (
        <div className="border border-[#E7E7ED] bg-white p-12 text-center" style={{ borderRadius: 6 }}>
          <p className="text-[#69738A]">คุณยังไม่เคยส่งคำขอ In-house</p>
          <Link to="/courses" className="mt-2 inline-block text-sm text-accent hover:underline">ดูคอร์สทั้งหมด</Link>
        </div>
      ) : (
        <div className="divide-y divide-[#E7E7ED] border border-[#E7E7ED] bg-white" style={{ borderRadius: 6 }}>
          {requests.map((req) => {
            const status = requestDisplayStatus(req)
            return (
              <div key={req.request_id} className="flex gap-5 p-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#F0EDFF]" style={{ borderRadius: 4 }}>
                  <FileText className="h-5 w-5 text-accent/60" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${status.dot}`} />
                    <span className="text-xs font-medium uppercase tracking-wide text-[#69738A]">{status.label}</span>
                    <span className="text-xs text-[#69738A]">· {req.request_number}</span>
                  </div>
                  <p className="mb-1 text-[15px] font-medium leading-snug text-[#19172A]">{req.course?.title}</p>
                  <p className="text-xs text-[#69738A]">
                    {req.trainee_count} คน · {formatDate(req.date_from)} - {formatDate(req.date_to)} · ส่งเมื่อ {formatDate(req.created_date)}
                  </p>
                </div>
                <Link
                  to={`/inhouse-requests/${req.request_id}`}
                  className="flex shrink-0 items-center gap-1 self-center text-sm font-medium text-accent hover:underline"
                >
                  {req.accepted_at ? 'ดูเอกสาร' : 'ยืนยันรับใบเสนอราคา'} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
